import { Button, Flex, Heading, Select, useToast } from "@chakra-ui/react";
import { Post } from "api";
import { useConfirmDialog } from "components/confirmDialog";
import React, { useCallback, useState } from "react";
import {
  CURRENT_MONTH,
  CURRENT_YEAR,
  MONTHS,
  YEARS,
} from "views/admin/partnerDetail/variables/dateValues";

interface ResendInvoiceViewProps {
  partnerId: number;
}

const ResendInvoiceView = (props: ResendInvoiceViewProps) => {
  const { partnerId } = props;
  const toast = useToast();

  const [invoiceYear, setInvoiceYear] = useState<string | undefined>(undefined);
  const [invoiceMonth, setInvoiceMonth] = useState<string | undefined>(
    undefined
  );
  const [invoiceSending, setInvoiceSending] = useState<boolean>(false);

  const {
    ConfirmDialog: ResendInvoiceConfirmDialog,
    openDialog: openResendInvoiceDialog,
  } = useConfirmDialog({
    title: "Invoice will be resend.",
    description: `Are you sure you want to resend the invoice for ${
      invoiceMonth ?? ""
    }/${invoiceYear ?? ""}?`,
  });

  const onResendInvoiceClick = useCallback(async () => {
    if (!partnerId || !invoiceYear || !invoiceMonth) return;

    const result = await openResendInvoiceDialog();

    if (!result) return;

    try {
      setInvoiceSending(true);
      const resendInvoiceResponse = await Post(
        `/manage/partners/${partnerId}/invoice-send`,
        {
          year: Number(invoiceYear),
          month: Number(invoiceMonth),
        }
      );

      if (resendInvoiceResponse.err) {
        toast({
          status: "error",
          title: "Invoice not sent",
          description: resendInvoiceResponse.err,
        });
      } else {
        toast({
          status: "success",
          title: "Success",
          description: "Invoice sent successfully.",
        });
      }
    } catch (error: any) {
      console.warn(error);
    } finally {
      setInvoiceSending(false);
    }
  }, [partnerId, invoiceYear, invoiceMonth, toast]);

  return (
    <Flex
      p={4}
      flexDir={"column"}
      mt={8}
      borderRadius={"16"}
      backgroundColor="white"
    >
      <Heading fontSize={"xl"}>Resend Invoice</Heading>
      <Flex mt={4} gap={4} alignItems="center">
        <Select
          backgroundColor={"white"}
          placeholder="Year"
          value={invoiceYear}
          onChange={(e) => {
            setInvoiceYear(e.target.value);
            setInvoiceMonth(undefined);
          }}
        >
          {YEARS.map((year) => (
            <option value={year}>{year}</option>
          ))}
        </Select>
        <Select
          backgroundColor={"white"}
          placeholder="Month"
          value={invoiceMonth ?? ""}
          onChange={(e) => setInvoiceMonth(e.target.value)}
          disabled={!invoiceYear}
        >
          {MONTHS.map((month) =>
            Number(invoiceYear) === CURRENT_YEAR &&
            Number(month) >= CURRENT_MONTH ? null : (
              <option value={month}>{month}</option>
            )
          )}
        </Select>
        <Flex>
          <Button
            bgColor={"brand.500"}
            color="white"
            _hover={{ backgroundColor: "brand.600" }}
            onClick={onResendInvoiceClick}
            disabled={!invoiceYear || !invoiceMonth}
            isLoading={invoiceSending}
          >
            Resend Invoice
          </Button>
        </Flex>
      </Flex>
      {ResendInvoiceConfirmDialog}
    </Flex>
  );
};

export default ResendInvoiceView;
